import { apiFetch } from "./client";
import type { Page } from "./admin";

export type ProductStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";
export type AssetKind =
  | "IMAGE"
  | "DATASHEET_PDF"
  | "SPECIFICATION"
  | "COA"
  | "CERTIFICATE"
  | "DECLARATION";

export type CategoryNode = {
  id: string;
  slug: string;
  displayOrder: number;
  name: string;
  imageUrl: string | null;
  children: CategoryNode[];
};

export type AttributeSchemaEntry = {
  key: string;
  labelKey?: string;
  /** Per-locale display label, filled by the administrator. Falls back to
   * labelKey (an attr.* i18n key for the built-ins) and then to `key`. */
  labels?: Record<string, string>;
  type?: "string" | "number" | "select" | "multiselect" | "boolean";
  unit?: string;
  options?: string[];
  /** option value -> per-locale display label, filled by the administrator.
   * Same inner shape as `labels`; falls back to the industry.* i18n keys and
   * then to the raw option value. */
  optionLabels?: Record<string, Record<string, string>>;
  filterable?: boolean;
};

export type CategoryDetail = {
  id: string;
  slug: string;
  parentId: string | null;
  displayOrder: number;
  translations: Record<string, string>;
  attributeSchema: AttributeSchemaEntry[];
};

export type ProductSummary = {
  id: string;
  sku: string;
  categoryId: string | null;
  categorySlug: string | null;
  status: ProductStatus;
  name: string;
  listPrice: number | null;
  currency: string | null;
  /** The article's own unit of measure (e.g. "Kg"); null when the catalog does not know it. */
  unit: string | null;
  /** The article's VAT rate in percent, as the ERP charges it; null when unknown. */
  vatRate?: number | null;
  attributes: Record<string, unknown>;
  /** True for an article from the ERP: its status follows the ERP, so it is not published here. */
  fromErp?: boolean;
  /** The product's own picture for its card, signed for a short while; null when it has none. */
  imageUrl?: string | null;
};

export type ProductTranslation = {
  name: string;
  shortDescription: string | null;
  longDescription: string | null;
};

export type AssetView = {
  id: string;
  kind: AssetKind;
  filename: string;
  sizeBytes: number;
  contentType: string;
  displayOrder: number;
};

export type ProductDetail = {
  id: string;
  sku: string;
  categoryId: string | null;
  categorySlug: string | null;
  status: ProductStatus;
  attributes: Record<string, unknown>;
  /** The article's own unit of measure (e.g. "Kg"); null when the catalog does not know it. */
  unit?: string | null;
  translations: Record<string, ProductTranslation>;
  assets: AssetView[];
  createdAt: string;
  updatedAt: string;
  /** True for an article from the ERP: its status and category follow the ERP, not the editor. */
  fromErp?: boolean;
};

const q = (params: Record<string, string | number | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") usp.set(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export const catalogApi = {
  // Public read (any authenticated user)
  tree: (locale = "en") => apiFetch<CategoryNode[]>(`/catalog/categories${q({ locale })}`),
  listProducts: (params: { categoryId?: string; status?: ProductStatus; locale?: string; page?: number; size?: number; sort?: string; includeDescendants?: boolean } = {}) =>
    apiFetch<Page<ProductSummary>>(
      `/catalog/products${q({
        categoryId: params.categoryId,
        status: params.status,
        locale: params.locale,
        page: params.page?.toString(),
        size: params.size?.toString(),
        sort: params.sort ?? "createdAt,desc",
        includeDescendants: params.includeDescendants ? "true" : undefined,
      })}`,
    ),
  searchProducts: (term: string, locale = "en", limit = 20) =>
    apiFetch<ProductSummary[]>(`/catalog/products/search${q({ q: term, locale, limit })}`),
  productBySku: (sku: string) =>
    apiFetch<ProductDetail>(`/catalog/products/sku/${encodeURIComponent(sku)}`),

  // Admin
  getCategory: (id: string) => apiFetch<CategoryDetail>(`/admin/catalog/categories/${id}`),
  createCategory: (body: {
    slug: string;
    parentId?: string;
    displayOrder?: number;
    translations: Record<string, string>;
    attributeSchema?: AttributeSchemaEntry[];
  }) => apiFetch<CategoryDetail>("/admin/catalog/categories", { method: "POST", body }),
  updateCategory: (id: string, body: Partial<CategoryDetail>) =>
    apiFetch<CategoryDetail>(`/admin/catalog/categories/${id}`, { method: "PUT", body }),
  deleteCategory: (id: string) =>
    apiFetch<void>(`/admin/catalog/categories/${id}`, { method: "DELETE" }),
  reorderCategory: (id: string, displayOrder: number) =>
    apiFetch<CategoryDetail>(`/admin/catalog/categories/${id}/order`, {
      method: "PATCH",
      body: { displayOrder },
    }),

  getProduct: (id: string) => apiFetch<ProductDetail>(`/admin/catalog/products/${id}`),
  createProduct: (body: {
    sku: string;
    categoryId: string;
    status?: ProductStatus;
    attributes?: Record<string, unknown>;
    translations?: Record<string, ProductTranslation>;
  }) => apiFetch<ProductDetail>("/admin/catalog/products", { method: "POST", body }),
  updateProduct: (id: string, body: Partial<ProductDetail>) =>
    apiFetch<ProductDetail>(`/admin/catalog/products/${id}`, { method: "PUT", body }),
  publishProduct: (id: string) =>
    apiFetch<void>(`/admin/catalog/products/${id}/publish`, { method: "POST" }),
  unpublishProduct: (id: string) =>
    apiFetch<void>(`/admin/catalog/products/${id}/unpublish`, { method: "POST" }),
  deleteProduct: (id: string) =>
    apiFetch<void>(`/admin/catalog/products/${id}`, { method: "DELETE" }),

  uploadAsset: async (productId: string, kind: AssetKind, file: File): Promise<AssetView> => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const res = await fetch(`/api/admin/catalog/products/${productId}/assets?kind=${kind}`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      let body: { code?: string; message?: string } = {};
      try { body = (await res.json()) as { code?: string; message?: string }; } catch { /* ignore */ }
      throw {
        status: res.status,
        code: body.code ?? `HTTP_${res.status}`,
        message: body.message ?? res.statusText,
      };
    }
    return (await res.json()) as AssetView;
  },
  reorderAsset: (productId: string, assetId: string, displayOrder: number) =>
    apiFetch<AssetView>(`/admin/catalog/products/${productId}/assets/${assetId}`, {
      method: "PATCH",
      body: { displayOrder },
    }),
  deleteAsset: (productId: string, assetId: string) =>
    apiFetch<void>(`/admin/catalog/products/${productId}/assets/${assetId}`, { method: "DELETE" }),
  presignAsset: (productId: string, assetId: string) =>
    apiFetch<{ url: string; ttlSeconds: string }>(
      `/admin/catalog/products/${productId}/assets/${assetId}/download-url`,
    ),
};
